const DESIGNER_WORK_BREAKPOINT = 900;

class DesignerWork extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-designer-work-list]');
    if (!this.list) return;

    this.root = this;
    this.actions = this.querySelector('[data-designer-work-actions]');
    this.button = this.querySelector('[data-designer-work-toggle]');
    this.label = this.querySelector('[data-designer-work-label]');
    this.items = Array.from(this.list.children);
    this.expanded = false;
    this.wasDesktop = this.isDesktop();

    this.handleResize = this.handleResize.bind(this);
    this.handleClick = this.handleClick.bind(this);

    if (this.button) this.button.addEventListener('click', this.handleClick);
    window.addEventListener('resize', this.handleResize);

    this.collapse();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    if (this.button) this.button.removeEventListener('click', this.handleClick);
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
  }

  refresh() {
    this.disconnectedCallback();
    this.connectedCallback();
  }

  isDesktop() {
    return window.matchMedia('(min-width: ' + DESIGNER_WORK_BREAKPOINT + 'px)').matches;
  }

  get initialCount() {
    const raw = this.isDesktop() ? this.dataset.initialCount : this.dataset.initialCountMobile;
    const count = parseInt(raw, 10);
    return Number.isFinite(count) && count > 0 ? count : this.items.length;
  }

  // Resize only matters when it crosses the desktop breakpoint.
  handleResize() {
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      const isDesktop = this.isDesktop();
      if (isDesktop === this.wasDesktop) return;
      this.wasDesktop = isDesktop;
      if (this.expanded) {
        this.expand();
      } else {
        this.collapse();
      }
    }, 150);
  }

  handleClick() {
    if (this.expanded && this.dataset.showSeeLess === 'true') {
      this.collapse(true);
    } else if (!this.expanded) {
      this.expand();
    }
  }

  expand() {
    const wasFocused = document.activeElement === this.button;
    const focusTarget = this.items[this.initialCount] || null;
    this.expanded = true;
    this.items.forEach((item) => {
      if (item.hasAttribute('hidden')) item.classList.add('is-revealed');
      item.removeAttribute('hidden');
    });
    this.root.classList.add('is-expanded');
    this.updateButton();
    if (wasFocused && focusTarget && this.actions?.hasAttribute('hidden')) {
      if (!focusTarget.hasAttribute('tabindex')) focusTarget.setAttribute('tabindex', '-1');
      focusTarget.focus({ preventScroll: true });
    }
  }

  collapse(shouldScroll) {
    const limit = this.initialCount;
    this.expanded = false;
    this.items.forEach((item, index) => {
      item.classList.remove('is-revealed');
      if (index < limit) {
        item.removeAttribute('hidden');
      } else {
        item.setAttribute('hidden', '');
      }
    });
    this.root.classList.remove('is-expanded');
    this.updateButton();
    if (shouldScroll) this.scrollIntoViewIfNeeded();
  }

  updateButton() {
    if (!this.button || !this.actions) return;

    const canExpand = !this.expanded && this.items.length > this.initialCount;
    const canCollapse = this.expanded && this.dataset.showSeeLess === 'true';

    if (canExpand || canCollapse) {
      this.actions.removeAttribute('hidden');
    } else {
      this.actions.setAttribute('hidden', '');
    }

    this.button.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');

    if (!this.label) return;
    const next = this.expanded && this.dataset.showSeeLess === 'true'
      ? this.dataset.seeLessLabel
      : this.dataset.seeMoreLabel;
    if (next) this.label.textContent = next;
  }

  scrollIntoViewIfNeeded() {
    const top = this.getBoundingClientRect().top;
    if (top >= 0) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }
}

if (!customElements.get('designer-work')) {
  customElements.define('designer-work', DesignerWork);
}

if (window.Shopify && window.Shopify.designMode && !window.designerWorkSectionLoadBound) {
  window.designerWorkSectionLoadBound = true;
  document.addEventListener('shopify:section:load', (event) => {
    const sections = event.target.querySelectorAll('designer-work');
    sections.forEach((section) => {
      if (typeof section.refresh === 'function') section.refresh();
    });
  });
}
